import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  CONTACT_FIELD_TYPES,
  CONTACT_TYPE_SECTIONS,
  CONTACT_TYPE_CATEGORY_LABELS,
  type ContactTypeFieldRow,
} from '@ehr/shared';
import { Dd, type DdOption } from '../components/Dropdown.js';
import { api } from '../lib/api.js';

/**
 * Contact Type field-template designer (V34 renderContactTypeForm). Opened from
 * the Contact Types manager pencil. Edits the type name (locked for system
 * types), its pipeline-category link, and a sectioned, drag-reorderable list of
 * custom fields. Persists each change to /contact-types/:id(/fields). 1:1 with
 * the mockup's .ls-form-card / .ctf-* markup.
 */

const SUBTITLE =
  'The fields contacts of this type will have. Drag the handle to reorder; add your own per section.';
const CATEGORY_NOTE =
  'Links this type to a pipeline so its Status/Stage options in Advanced Search match that pipeline’s. Leave unlinked to keep today’s behavior (Dashboard view alone decides scope).';
const SYSTEM_NOTE =
  'System contact type — the name is locked. You can still edit its fields, toggle it active/inactive, or delete it.';

// Field-type picker (the mockup's 12 options + State, so seeded State fields show).
const TYPE_OPTIONS: DdOption[] = [
  ...CONTACT_FIELD_TYPES.map((t) => ({ value: t.value, label: t.label })),
  { value: 'state', label: 'State' },
];
const CATEGORY_OPTIONS: DdOption[] = [
  { value: '', label: '— Not linked —' },
  ...(
    Object.keys(CONTACT_TYPE_CATEGORY_LABELS) as (keyof typeof CONTACT_TYPE_CATEGORY_LABELS)[]
  ).map((k) => ({ value: k, label: CONTACT_TYPE_CATEGORY_LABELS[k] })),
];

const HANDLE = '⁙⁙';

export function ContactTypeFieldForm({
  type,
  onClose,
  onChanged,
}: {
  type: { id: string; name: string; system: boolean; category: string | null };
  onClose: () => void;
  onChanged: () => void;
}) {
  const [name, setName] = useState(type.name);
  const [category, setCategory] = useState(type.category ?? '');
  const [fields, setFields] = useState<ContactTypeFieldRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const dragId = useRef<string | null>(null);

  const base = `/contact-types/${type.id}`;

  const load = () => {
    setLoading(true);
    api
      .get<ContactTypeFieldRow[]>(`${base}/fields`)
      .then((rows) => {
        setFields(Array.isArray(rows) ? rows : []);
        setErr(null);
      })
      .catch(() => setErr('Could not load fields. Please try again.'))
      .finally(() => setLoading(false));
  };
  useEffect(load, [type.id]);

  useEffect(() => {
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  // ── Type-level edits ──
  const saveName = () => {
    const n = name.trim();
    if (type.system || !n || n === type.name) return;
    api
      .patch(`${base}`, { name: n })
      .then(onChanged)
      .catch(() => setErr('Could not rename.'));
  };
  const changeCategory = (v: string) => {
    setCategory(v);
    api
      .patch(`${base}`, { category: v || null })
      .then(onChanged)
      .catch(() => setErr('Could not link category.'));
  };

  // ── Field CRUD ──
  const addField = (section: string) => {
    api
      .post<ContactTypeFieldRow>(`${base}/fields`, {
        label: 'New field',
        fieldType: 'text',
        section,
      })
      .then((row) => {
        // Append in place (no full-reload spinner / scroll reset), then bring
        // the new field into view and focus its label so the add is visible —
        // the header button targets "Other Fields", which sits at the bottom.
        setFields((fs) => [...fs, row]);
        onChanged();
        requestAnimationFrame(() => {
          const el = document.querySelector<HTMLInputElement>(`[data-fid="${row.id}"] .ctf-label`);
          if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            el.focus();
            el.select();
          }
        });
      })
      .catch(() => setErr('Could not add a field.'));
  };
  const patchField = (fid: string, patch: Partial<ContactTypeFieldRow>) =>
    api.patch(`${base}/fields/${fid}`, patch).catch(() => setErr('Could not save the field.'));

  const setLocal = (fid: string, patch: Partial<ContactTypeFieldRow>) =>
    setFields((fs) => fs.map((f) => (f.id === fid ? { ...f, ...patch } : f)));

  const changeType = (fid: string, fieldType: string) => {
    setLocal(fid, { fieldType: fieldType as ContactTypeFieldRow['fieldType'] });
    patchField(fid, { fieldType: fieldType as ContactTypeFieldRow['fieldType'] });
  };
  const deleteField = (fid: string) => {
    api
      .del(`${base}/fields/${fid}`)
      .then(() => {
        setFields((fs) => fs.filter((f) => f.id !== fid));
        onChanged();
      })
      .catch(() => setErr('Could not delete the field.'));
  };

  // Options (select / checkboxes)
  const commitOptions = (fid: string, options: string[]) => {
    setLocal(fid, { options });
    patchField(fid, { options });
  };
  const addOption = (f: ContactTypeFieldRow) => commitOptions(f.id, [...f.options, '']);
  const setOption = (f: ContactTypeFieldRow, i: number, val: string) =>
    setLocal(f.id, { options: f.options.map((o, oi) => (oi === i ? val : o)) });
  const saveOption = (f: ContactTypeFieldRow) => patchField(f.id, { options: f.options });
  const removeOption = (f: ContactTypeFieldRow, i: number) =>
    commitOptions(
      f.id,
      f.options.filter((_, oi) => oi !== i),
    );

  // ── Drag reorder (flat order; a drop also adopts the target's section) ──
  const onDrop = (targetId: string | null, section: string) => {
    const dId = dragId.current;
    dragId.current = null;
    if (!dId || dId === targetId) return;
    const next = fields.slice();
    const di = next.findIndex((f) => f.id === dId);
    if (di < 0) return;
    const [moved] = next.splice(di, 1);
    moved!.section = section;
    if (targetId) {
      const ti = next.findIndex((f) => f.id === targetId);
      next.splice(ti < 0 ? next.length : ti, 0, moved!);
    } else {
      // dropped on the section body: place after the last field of that section
      let last = -1;
      next.forEach((f, i) => {
        if (f.section === section) last = i;
      });
      next.splice(last + 1, 0, moved!);
    }
    setFields(next);
    Promise.resolve(patchField(moved!.id, { section }))
      .then(() => api.patch(`${base}/fields/reorder`, { ids: next.map((f) => f.id) }))
      .catch(() => setErr('Could not reorder.'));
  };

  // Sections: the four seeded ones always show; extra sections only if populated.
  const extra = Array.from(new Set(fields.map((f) => f.section))).filter(
    (s) => !(CONTACT_TYPE_SECTIONS as readonly string[]).includes(s),
  );
  const sections = [...CONTACT_TYPE_SECTIONS, ...extra];

  const body = (
    <div className="ls-form-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div
        className="ls-form-card"
        role="dialog"
        aria-modal="true"
        aria-label={name || 'Contact Type'}
      >
        <div className="ls-form-head">
          <div>
            <h2>{name.trim() || 'New Contact Type'}</h2>
            <p>{SUBTITLE}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0 }}>
            <button
              type="button"
              className="btn primary"
              style={{ fontSize: 12.5, padding: '7px 14px', whiteSpace: 'nowrap' }}
              onClick={() => addField('Other Fields')}
            >
              + Add field
            </button>
            <button className="modal-close" onClick={onClose} aria-label="Close">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        </div>

        <div className="ls-form-body">
          <div className="ls-fld ctf-name-fld">
            <label>Contact Type Name *</label>
            <input
              className="ctf-name"
              value={name}
              disabled={type.system}
              placeholder="e.g. Vendor"
              onChange={(e) => setName(e.target.value)}
              onBlur={saveName}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLInputElement).blur()}
            />
            {type.system && <div className="ctf-name-note">{SYSTEM_NOTE}</div>}
          </div>

          <div className="ls-fld ctf-name-fld">
            <label>Pipeline Category</label>
            <Dd
              value={category}
              options={CATEGORY_OPTIONS}
              onChange={changeCategory}
              searchable={false}
            />
            <div className="ctf-name-note">{CATEGORY_NOTE}</div>
          </div>

          {err && <div className="config-err">{err}</div>}
          {loading ? (
            <p className="muted">Loading…</p>
          ) : (
            sections.map((sec) => {
              const fs = fields.filter((f) => f.section === sec);
              return (
                <div className="ctf-section" key={sec}>
                  <div className="ctf-section-head">{sec}</div>
                  <div
                    className="ctf-list"
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                      e.preventDefault();
                      onDrop(null, sec);
                    }}
                  >
                    {fs.length === 0 ? (
                      <div className="ctf-empty">
                        No fields here yet — add one below or drag one in.
                      </div>
                    ) : (
                      fs.map((f) => (
                        <div
                          className="ctf-field"
                          key={f.id}
                          data-fid={f.id}
                          draggable
                          onDragStart={(e) => {
                            dragId.current = f.id;
                            e.currentTarget.classList.add('ctf-dragging');
                          }}
                          onDragEnd={(e) => e.currentTarget.classList.remove('ctf-dragging')}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.currentTarget.classList.add('ctf-drop-before');
                          }}
                          onDragLeave={(e) => e.currentTarget.classList.remove('ctf-drop-before')}
                          onDrop={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            e.currentTarget.classList.remove('ctf-drop-before');
                            onDrop(f.id, sec);
                          }}
                        >
                          <div className="ctf-row">
                            <span className="ctf-handle" aria-hidden title="Drag to reorder">
                              {HANDLE}
                            </span>
                            <input
                              className="ctf-label"
                              value={f.label}
                              placeholder="Field label"
                              onChange={(e) => setLocal(f.id, { label: e.target.value })}
                              onBlur={(e) => patchField(f.id, { label: e.target.value })}
                            />
                            <div className="ctf-typewrap">
                              <Dd
                                value={f.fieldType}
                                options={TYPE_OPTIONS}
                                onChange={(v) => changeType(f.id, v)}
                                searchable={false}
                              />
                            </div>
                            <button
                              type="button"
                              className="btn danger ctf-del"
                              title="Delete field"
                              onClick={() => deleteField(f.id)}
                            >
                              ✕
                            </button>
                          </div>

                          {(f.fieldType === 'select' || f.fieldType === 'checkboxes') && (
                            <div className="ctf-options">
                              <div className="ctf-options-label">
                                Options{' '}
                                {f.fieldType === 'checkboxes' ? '(pick multiple)' : '(pick one)'}
                              </div>
                              {f.options.map((o, oi) => (
                                <div className="ctf-opt" key={oi}>
                                  <input
                                    value={o}
                                    placeholder={`Option ${oi + 1}`}
                                    onChange={(e) => setOption(f, oi, e.target.value)}
                                    onBlur={() => saveOption(f)}
                                  />
                                  <button
                                    type="button"
                                    className="ctf-opt-x"
                                    title="Remove option"
                                    onClick={() => removeOption(f, oi)}
                                  >
                                    ✕
                                  </button>
                                </div>
                              ))}
                              <button
                                type="button"
                                className="ctf-opt-add"
                                onClick={() => addOption(f)}
                              >
                                + Add option
                              </button>
                            </div>
                          )}
                          {f.fieldType === 'leadsourcetype' && (
                            <div className="ctf-srcnote">
                              Options come from your Source Types (in Referrals/Lead Sources)
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                  <button type="button" className="ctf-add" onClick={() => addField(sec)}>
                    + Add field
                  </button>
                </div>
              );
            })
          )}
        </div>

        <div className="ls-form-foot">
          <button className="btn" onClick={onClose}>
            Close
          </button>
          <button className="btn primary" onClick={onClose}>
            Save
          </button>
        </div>
      </div>
    </div>
  );
  return createPortal(body, document.body);
}
